/**
 * CampaignForm — Orchestrator component that renders all section components
 * based on campaignType. Shared across Create, Modify, and View flows.
 * The page shell (e.g. CreateCampaignPage) calls useCampaignForm() for state,
 * then passes the result here for rendering.
 */

import { FC } from 'react';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { CampaignStatus, CampaignType } from 'src/@types/valueMatching/enums';
import { CampaignConfigurationData } from 'src/@types/valueMatching/campaignConfiguration';
import { CAMPAIGN_CONFIG } from 'src/routes/valueMatching/routes';
import { UseCampaignFormResult } from 'src/hooks/useCampaignForm';
import { FieldKey } from 'src/utils/valueMatching/form/formValidationUtils';
import { parseAsins } from 'src/utils/valueMatching/form/formDataBuilder';
import { FormMode } from './types';
import { useTouchedSetters } from './useTouchedSetters';
import CampaignDetailsSection from './sections/CampaignDetailsSection';
import MerchantGroupSection from './sections/MerchantGroupSection';
import AsinInclusionSection from './sections/AsinInclusionSection';
import FundingAccountSection from './sections/FundingAccountSection';
import SchedulingSection from './sections/SchedulingSection';
import SuspendedProductsSection from './sections/SuspendedProductsSection';
import NcemiFundingSharesSection from './sections/NcemiFundingSharesSection';
import NcemiBankCardSection from './sections/NcemiBankCardSection';
import NcemiPriceLeadingSection from './sections/NcemiPriceLeadingSection';
import CashbackSection from './sections/CashbackSection';
import BuybackSection from './sections/BuybackSection';
import DealGuardrailSection from './sections/DealGuardrailSection';

export interface CampaignFormProps {
    /** The form mode — determines field editability */
    mode: FormMode;
    /** Form state from useCampaignForm hook */
    form: UseCampaignFormResult;
    /** Campaign configuration from the API */
    config: CampaignConfigurationData | null;
    /** Resolved campaign type */
    campaignType: CampaignType | undefined;
    /** Whether to show the merchant group section (NCEMI/DEAL) */
    showMerchantGroup: boolean;
    /** Per-field validation error messages (already translated), keyed by field. */
    errors?: Partial<Record<FieldKey, string>>;
    /** Marks a field as touched; called the first time the user edits that field. */
    markTouched?: (field: FieldKey) => void;
    /** Suspended product ASINs (shown in view/modify under Seller Participation Conditions) */
    suspendedAsins?: string[];
    /** Campaign status — controls startDate/startTime editability in modify mode */
    campaignStatus?: CampaignStatus;
}

const CampaignForm: FC<CampaignFormProps> = ({
    mode,
    form,
    config,
    campaignType,
    showMerchantGroup,
    errors = {},
    markTouched,
    suspendedAsins,
    campaignStatus,
}) => {
    const campaignTypeName = campaignType ? CAMPAIGN_CONFIG[campaignType].displayName : '';

    // Touch-aware setters that flip the corresponding `touched` flag before
    // delegating to the raw form setter.
    const setters = useTouchedSetters(form, markTouched);

    return (
        <SpaceBetween size={mode === 'view' ? 's' : 'l'}>
            <CampaignDetailsSection title={form.title} setTitle={setters.setTitle} mode={mode} errorText={errors.title} />
            {showMerchantGroup && (
                <MerchantGroupSection
                    selectedMerchantGroup={form.selectedMerchantGroup}
                    merchantGroupOptions={form.merchantGroupOptions}
                    handleMerchantGroupChange={form.handleMerchantGroupChange}
                    mode={mode}
                />
            )}
            <AsinInclusionSection
                asins={form.asins}
                setAsins={setters.setAsins}
                campaignType={campaignType}
                mode={mode}
                errorText={errors.asins}
                suspendedAsins={suspendedAsins}
                excludedOldItemIds={form.isBuyback ? form.excludedOldItemIds : undefined}
                setExcludedOldItemIds={form.isBuyback ? form.setExcludedOldItemIds : undefined}
            />
            {mode === 'modify'
                && (campaignType === CampaignType.NO_COST_EMI || campaignType === CampaignType.DEAL) && (
                    <SuspendedProductsSection
                        mode="modify"
                        asins={parseAsins(form.suspendedAsins)}
                        suspendedAsins={form.suspendedAsins}
                        setSuspendedAsins={form.setSuspendedAsins}
                        errorText={errors.suspendedAsins}
                    />
                )}
            {/*
              * Hide the Funding Account section for NO_COST_EMI and DEAL
              * campaigns — these campaign types do not surface a funding
              * account selection in the UI. Only CASHBACK and BUYBACK show it.
              */}
            {campaignType !== CampaignType.NO_COST_EMI && campaignType !== CampaignType.DEAL && (
                <FundingAccountSection
                    selectedFundingAccount={form.selectedFundingAccount}
                    setSelectedFundingAccount={form.setSelectedFundingAccount}
                    fundingAccountOptions={form.fundingAccountOptions}
                    mode={mode}
                />
            )}
            {form.isNcemi && (
                <NcemiFundingSharesSection
                    sellerShare={String(form.sellerShare ?? '')}
                    setSellerShare={setters.setSellerShareStr}
                    brandShare={String(form.brandShare ?? '')}
                    setBrandShare={setters.setBrandShareStr}
                    bankShare={String(form.bankShare ?? '')}
                    setBankShare={setters.setBankShareStr}
                    amazonShare={String(form.amazonShare ?? '')}
                    setAmazonShare={setters.setAmazonShareStr}
                    brandName={form.brandName} setBrandName={setters.setBrandName}
                    brandNameOptions={form.brandNameOptions}
                    role={form.role}
                    mode={mode}
                    sellerShareError={errors.sellerShare}
                    brandShareError={errors.brandShare}
                    bankShareError={errors.bankShare}
                    amazonShareError={errors.amazonShare}
                    brandNameError={errors.brandName}
                    fundingSharesSumError={errors.fundingSharesSum}
                />
            )}
            {form.isNcemi && (
                <NcemiBankCardSection
                    creditCardOptions={form.creditCardOptions}
                    cbccCardOptions={form.cbccCardOptions} bflCardOptions={form.bflCardOptions}
                    selectedCreditCards={form.selectedCreditCards} setSelectedCreditCards={form.setSelectedCreditCards}
                    selectedCbccCards={form.selectedCbccCards} setSelectedCbccCards={form.setSelectedCbccCards}
                    selectedBflCards={form.selectedBflCards} setSelectedBflCards={form.setSelectedBflCards}
                    creditCardTenure={form.creditCardTenure} setCreditCardTenure={form.setCreditCardTenure}
                    cbccCardTenure={form.cbccCardTenure} setCbccCardTenure={form.setCbccCardTenure}
                    bflCardTenure={form.bflCardTenure} setBflCardTenure={form.setBflCardTenure}
                    creditTenureOptions={form.creditTenureOptions}
                    cbccTenureOptions={form.cbccTenureOptions} bflTenureOptions={form.bflTenureOptions}
                    mode={mode}
                />
            )}
            {form.isNcemi && (
                <NcemiPriceLeadingSection
                    priceLeadingGuardrail={form.priceLeadingGuardrail}
                    setPriceLeadingGuardrail={setters.setPriceLeadingGuardrail}
                    mode={mode}
                    errorText={errors.priceLeadingGuardrail}
                />
            )}
            {form.isCashback && (
                <CashbackSection
                    primeRestriction={form.primeRestriction} setPrimeRestriction={form.setPrimeRestriction}
                    termsAndConditions={form.termsAndConditions} setTermsAndConditions={setters.setTermsAndConditions}
                    cashbackExtentGuardrail={form.cashbackExtentGuardrail} setCashbackExtentGuardrail={setters.setCashbackExtentGuardrail}
                    cashbackPercentLimitGuardrail={form.cashbackPercentLimitGuardrail} setCashbackPercentLimitGuardrail={setters.setCashbackPercentLimitGuardrail}
                    cashbackAbsoluteLimitGuardrail={form.cashbackAbsoluteLimitGuardrail} setCashbackAbsoluteLimitGuardrail={setters.setCashbackAbsoluteLimitGuardrail}
                    roundingOption={form.roundingOption} setRoundingOption={form.setRoundingOption}
                    roundingOptions={form.roundingOptions}
                    mode={mode}
                    termsAndConditionsError={errors.termsAndConditions}
                    cashbackExtentGuardrailError={errors.cashbackExtentGuardrail}
                    cashbackPercentLimitGuardrailError={errors.cashbackPercentLimitGuardrail}
                    cashbackAbsoluteLimitGuardrailError={errors.cashbackAbsoluteLimitGuardrail}
                />
            )}
            {form.isBuyback && (
                <BuybackSection
                    brandSweetenerOption={form.brandSweetenerOption} setBrandSweetenerOption={form.setBrandSweetenerOption}
                    brandSweetenerOptions={form.brandSweetenerOptions}
                    buybackPercentOfAsinPrice={form.buybackPercentOfAsinPrice}
                    setBuybackPercentOfAsinPrice={setters.setBuybackPercentOfAsinPrice}
                    buybackPercentOfBuybackOffer={form.buybackPercentOfBuybackOffer}
                    setBuybackPercentOfBuybackOffer={setters.setBuybackPercentOfBuybackOffer}
                    buybackAbsoluteValueCap={form.buybackAbsoluteValueCap}
                    setBuybackAbsoluteValueCap={setters.setBuybackAbsoluteValueCap}
                    mode={mode}
                    buybackPercentOfAsinPriceError={errors.buybackPercentOfAsinPrice}
                    buybackPercentOfBuybackOfferError={errors.buybackPercentOfBuybackOffer}
                    buybackAbsoluteValueCapError={errors.buybackAbsoluteValueCap}
                />
            )}
            {form.isDeal && (
                <DealGuardrailSection
                    dealPercentOfOriginalDealPrice={form.dealPercentOfOriginalDealPrice}
                    setDealPercentOfOriginalDealPrice={setters.setDealPercentOfOriginalDealPrice}
                    mode={mode}
                    errorText={errors.dealPercentOfOriginalDealPrice}
                />
            )}
            <SchedulingSection
                startDate={form.startDate} setStartDate={setters.setStartDate}
                startTime={form.startTime} setStartTime={setters.setStartTime}
                endDate={form.endDate} setEndDate={setters.setEndDate}
                endTime={form.endTime} setEndTime={setters.setEndTime}
                propagationDelayInMinutes={config?.propagationDelayInMinutes ?? 0}
                campaignTypeName={campaignTypeName}
                mode={mode}
                startDateTimeError={errors.startDateTime}
                endDateTimeError={errors.endDateTime}
                campaignStatus={campaignStatus}
                campaignType={campaignType}
            />
        </SpaceBetween>
    );
};

export default CampaignForm;
